/**
 * Workbook import: turns the CSV tabs exported from the owner's
 * "Business Transaction" spreadsheet (OneDrive / Excel) into records for the
 * owner workspace — contractors, expenses, insurance policies, tenants and
 * Secure Vault logins.
 *
 * Everything in this module runs in the browser and is pure (no network, no
 * storage). Parsed rows — including vault passwords — only live in memory
 * until the owner confirms the import and they are sent to their own Convex
 * account. Nothing is cached, persisted or logged here.
 */

// ---------------------------------------------------------------------------
// Field specs (drive the editable preview table)
// ---------------------------------------------------------------------------

export const EXPENSE_CATEGORIES = [
  { value: "maintenance", label: "Maintenance / repair" },
  { value: "credit card payment", label: "Credit card payment" },
  { value: "mortgage", label: "Mortgage" },
  { value: "insurance", label: "Insurance" },
  { value: "utilities", label: "Utilities" },
  { value: "property tax", label: "Property tax" },
  { value: "hoa", label: "HOA" },
  { value: "bank fee", label: "Bank fee" },
  { value: "other", label: "Other" },
];

export const VAULT_SECTIONS = [
  { value: "operations", label: "Logins" },
  { value: "bills", label: "Bills & utilities" },
  { value: "banking", label: "Bank & cards" },
];

export const KIND_LABELS = {
  contractors: "Contractors",
  expenses: "Expenses",
  insurance: "Insurance policies",
  tenants: "Tenants",
  vault: "Secure vault",
};

export const KIND_DESTINATIONS = {
  contractors: "/contractors",
  expenses: "/finance?tab=transactions",
  insurance: "/insurance",
  tenants: "/tenants",
  vault: "/vault",
};

export const FIELD_SPECS = {
  contractors: [
    { key: "companyName", label: "Company" },
    { key: "contactName", label: "Contact" },
    { key: "specialty", label: "Work type" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "website", label: "Website" },
    { key: "address", label: "Address" },
    { key: "notes", label: "Notes", wide: true },
  ],
  expenses: [
    { key: "date", label: "Date", type: "date" },
    { key: "propertyLabel", label: "Property" },
    { key: "title", label: "Description", wide: true },
    { key: "category", label: "Category", type: "select", options: EXPENSE_CATEGORIES },
    { key: "amount", label: "Amount", type: "money" },
    { key: "notes", label: "Notes", wide: true },
  ],
  insurance: [
    { key: "propertyLabel", label: "Property", wide: true },
    { key: "insuranceName", label: "Insurer" },
    { key: "policyNumber", label: "Policy #" },
    { key: "termStart", label: "Term start", type: "date" },
    { key: "termEnd", label: "Term end", type: "date" },
    { key: "premium", label: "Premium", type: "money" },
    { key: "agent", label: "Agent" },
    { key: "notes", label: "Notes", wide: true },
  ],
  tenants: [
    { key: "firstName", label: "First name" },
    { key: "lastName", label: "Last name" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "propertyLabel", label: "Property" },
    { key: "leaseStartDate", label: "Lease start", type: "date" },
    { key: "leaseEndDate", label: "Lease end", type: "date" },
    { key: "monthlyRent", label: "Rent", type: "money" },
    { key: "securityDeposit", label: "Deposit", type: "money" },
    { key: "notes", label: "Notes", wide: true },
  ],
  vault: [
    { key: "section", label: "Section", type: "select", options: VAULT_SECTIONS },
    { key: "name", label: "Name", wide: true },
    { key: "loginEmail", label: "Login email" },
    { key: "username", label: "Username" },
    { key: "secret", label: "Password", type: "secret" },
    { key: "accountNumber", label: "Account #" },
    { key: "url", label: "Link" },
    { key: "notes", label: "Notes", wide: true },
  ],
};

export const TAB_LABELS = {
  contractors: "Contractors info",
  credentials: "Credentials",
  expenses: "Expenses",
  property: "Property sheet",
  insurance: "Insurance",
};

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

/**
 * RFC 4180 CSV parser that keeps quoted line breaks inside a cell and records
 * the 1-based source line each row starts on.
 */
export function parseCsvWithLines(text) {
  const src = String(text ?? "").replace(/^\uFEFF/, "");
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  let line = 1;
  let rowLine = 1;

  const endRow = () => {
    row.push(field);
    rows.push({ cells: row, line: rowLine });
    row = [];
    field = "";
  };

  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        if (ch === "\n") line += 1;
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\r" || ch === "\n") {
      if (ch === "\r" && src[i + 1] === "\n") i += 1;
      endRow();
      line += 1;
      rowLine = line;
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length > 0) {
    endRow();
  }
  return rows;
}

export function parseCsv(text) {
  return parseCsvWithLines(text).map((row) => row.cells);
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const clean = (value) => String(value ?? "").replace(/\s+/g, " ").trim();
const cellLines = (value) =>
  String(value ?? "")
    .split(/\r?\n/)
    .map(clean)
    .filter(Boolean);
const norm = (value) => clean(value).toLowerCase();
const normLoose = (value) => norm(value).replace(/[^a-z0-9]/g, "");
const isBlank = (cells) => cells.every((cell) => !clean(cell));
const pad = (n) => String(n).padStart(2, "0");
const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");
const phoneKey = (value) => digitsOnly(value).slice(0, 10);

function joinNotes(parts) {
  const seen = new Set();
  const out = [];
  for (const part of parts) {
    const text = clean(part);
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text);
  }
  return out.join(" · ");
}

function titleCase(value) {
  return clean(value)
    .toLowerCase()
    .split(" ")
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" ");
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_TYPO_RE = /^[^\s@]+@(gmail|yahoo|outlook|hotmail|icloud|aol)$/i;
const URL_RE = /^(https?:\/\/|www\.)\S+$/i;
const DOMAIN_RE = /^[a-z0-9][a-z0-9-]*(\.[a-z0-9-]+)*\.[a-z]{2,}(\/\S*)?$/i;
const PHONE_RE = /^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
const MONEY_RE = /^\$?\s?\d{1,3}(,\d{3})*(\.\d{2})?$|^\$?\s?\d+(\.\d{2})?$/;

export const looksLikeEmail = (value) =>
  EMAIL_RE.test(clean(value)) || EMAIL_TYPO_RE.test(clean(value));
export const looksLikeUrl = (value) => {
  const text = clean(value);
  if (!text || /\s/.test(text) || text.includes("@")) return false;
  return URL_RE.test(text) || DOMAIN_RE.test(text);
};
const looksLikePhone = (value) => PHONE_RE.test(clean(value));
const looksLikeMoney = (value) => /[.,]/.test(clean(value)) && MONEY_RE.test(clean(value));

export function parseMoney(value) {
  const text = clean(value);
  if (!text || /^[-–—]+$/.test(text)) return null;
  const match = text.replace(/,/g, "").match(/\d+(\.\d+)?/);
  if (!match) return null;
  const amount = Number.parseFloat(match[0]);
  return Number.isFinite(amount) ? amount : null;
}

function moneyString(value) {
  const amount = parseMoney(value);
  if (amount === null) return "";
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

const MONTHS = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
};

function toDateKey(year, month, day) {
  if (!year || !month || !day) return null;
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Parse "1/2/2026", "05/15/2026", "Jul 30, 2026", "2026-07-30" → "YYYY-MM-DD". */
export function parseSheetDate(value) {
  const text = clean(value);
  if (!text) return null;
  let match = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);
  if (match) {
    const year = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
    return toDateKey(year, Number(match[1]), Number(match[2]));
  }
  match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) {
    return toDateKey(Number(match[1]), Number(match[2]), Number(match[3]));
  }
  match = text.match(/^([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})$/);
  if (match) {
    const month = MONTHS[match[1].toLowerCase().slice(0, 4)] ?? MONTHS[match[1].toLowerCase().slice(0, 3)];
    return toDateKey(Number(match[3]), month, Number(match[2]));
  }
  return null;
}

/** Split "05/15/2026 to 05/15/2027" or "Jul 30, 2026 - Jul 30, 2027". */
export function parseTerm(value) {
  const parts = clean(value).split(/\s+(?:to|through|thru|-|–|—)\s+/i);
  if (parts.length !== 2) return { start: null, end: null };
  return { start: parseSheetDate(parts[0]), end: parseSheetDate(parts[1]) };
}

/** "YYYY-MM-DD" → local-noon timestamp (displays as the same day everywhere in the US). */
export function dateKeyToTimestamp(key) {
  const match = String(key ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12).getTime();
}

/** Timestamp → "YYYY-MM-DD" in local time (UTC-midnight seeds use their UTC day). */
export function timestampToDateKey(timestamp) {
  if (timestamp === null || timestamp === undefined || timestamp === "") return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  const utcMidnight =
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0;
  if (utcMidnight) {
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// ---------------------------------------------------------------------------
// Properties
// ---------------------------------------------------------------------------

/**
 * Stable key for matching sheet labels to properties: house number plus the
 * first four letters of the street ("12 MAPLE ROAD" and "12 Maple Rd."
 * → "12mapl"; a misspelt "40 Oakwod Ct" still matches "40 Oakwood Ct" → "40oakw").
 */
export function propertyKey(label) {
  const text = norm(label).replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
  const match = text.match(/^(\d+)\s+([a-z]+)/);
  if (!match) return null;
  return `${match[1]}${match[2].slice(0, 4)}`;
}

export function parseFullAddress(label) {
  const text = clean(label);
  const match = text.match(/^(.*?),\s*([^,]+?)\s*,?\s*([A-Za-z]{2})\s+(\d{5})(?:-\d{4})?$/);
  if (!match) return null;
  const address = clean(match[1]);
  return {
    name: address,
    address,
    city: titleCase(match[2]),
    state: match[3].toUpperCase(),
    zipCode: match[4],
  };
}

export function buildPropertyIndex(properties = []) {
  const index = new Map();
  for (const property of properties) {
    for (const label of [property.address, property.name]) {
      const key = propertyKey(label);
      if (key && !index.has(key)) index.set(key, property);
    }
  }
  return index;
}

/** Sheet labels like "Credit" (no house number) are business-wide, not a property. */
const isGeneralLabel = (label) => !propertyKey(label);

// ---------------------------------------------------------------------------
// Expense categories
// ---------------------------------------------------------------------------

const CATEGORY_RULES = [
  ["mortgage", /mortgage|morgage|loan servic|pennymac|mr\.? cooper|select portfolio/i],
  ["credit card payment", /credit bill|credit card|credit paid|amex|american express|discover|chase credit|chase ink/i],
  ["insurance", /insurance|premium/i],
  ["utilities", /\bbge\b|water bill|baltimore water|electric bill|gas bill|utilit/i],
  ["property tax", /property tax|real estate tax/i],
  ["hoa", /\bhoa\b|homeowners? assoc/i],
  ["bank fee", /monthly fee|service fee|bank fee|transfer fee|overdraft|overdrafted/i],
  [
    "maintenance",
    /repair|fix|plumb|paint|remodel|renovat|rennovat|kitchen|heat|hvac|install|disposal|exhaust|fan\b|stove|oven|fridge|dryer|washer|washing|appliance|lead|inspect|maint|mainence|roof|termite|pest/i,
  ],
];

/** First text that names a category wins (the sheet's Category column before the description). */
export function inferExpenseCategory(...texts) {
  for (const text of texts.map(clean).filter(Boolean)) {
    for (const [category, pattern] of CATEGORY_RULES) {
      if (pattern.test(text)) return category;
    }
  }
  return "other";
}

/** Sheet "Category" values that are just a category name, not extra detail. */
const PLAIN_CATEGORY_RE = /^(mortgage paid|mortgage|credit bills?|hoa|insurance|utilities|maintenance|other)$/i;

// ---------------------------------------------------------------------------
// Tab detection
// ---------------------------------------------------------------------------

export function detectTab(fileName, rows) {
  const header = (rows.find((row) => !isBlank(row.cells))?.cells || []).map(norm);
  const has = (label) => header.includes(label);
  if (has("work type")) return "contractors";
  if (has("home address") && has("insurance name")) return "insurance";
  if (header[0] === "date" && has("description") && has("amount")) {
    return has("tenants") || has("dob") ? "property" : "expenses";
  }

  const name = norm(fileName);
  if (/credential|password|login|\bcred/.test(name)) return "credentials";
  if (/contractor/.test(name)) return "contractors";
  if (/insurance/.test(name)) return "insurance";
  if (/expense|transaction/.test(name) && !/credential|contractor|insurance/.test(name)) {
    return "expenses";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tab parsers
// ---------------------------------------------------------------------------

let rowSeq = 0;
function makeRow(kind, line, data, extras = {}) {
  rowSeq += 1;
  return {
    id: `${kind}-${line}-${rowSeq}`,
    kind,
    line,
    data,
    warnings: extras.warnings || [],
    defaultSelected: extras.defaultSelected ?? true,
  };
}

function section(kind) {
  return { kind, rows: [], skipped: [] };
}

function skip(target, line, reason, cells) {
  target.skipped.push({
    line,
    reason,
    preview: cells.map(clean).filter(Boolean).join(" | ").slice(0, 120),
  });
}

const CONTRACTOR_TYPES = [
  [/waterproof|foundation|basement/i, ["Basement Waterproofing", "Foundation"]],
  [/handyman/i, ["Handyman"]],
  [/hvac|heat|a\/?c\b/i, ["HVAC"]],
  [/plumb/i, ["Plumbing"]],
  [/electric/i, ["Electrical"]],
  [/roof/i, ["Roofing"]],
  [/termite|pest/i, ["Termite", "Pest Control"]],
  [/appliance/i, ["Appliance Repair"]],
];

function contractorSpecialty(workType) {
  for (const [pattern, specialty] of CONTRACTOR_TYPES) {
    if (pattern.test(workType)) return specialty;
  }
  return workType ? [titleCase(workType)] : [];
}

const STREET_RE = /^\d+\s+.*\b(rd|road|st|street|ave|avenue|dr|drive|ct|court|cir|circle|ln|lane|blvd|way|pl|place|pkwy|hwy|ste|suite)\b/i;
const looksLikeAddressLine = (text) => STREET_RE.test(text) || /,\s*[A-Za-z]{2}\s+\d{5}/.test(text);

const GENERIC_TRADE_RE = /^(handyman|plumbing|plumber|ac\/heat|hvac|heat|electrical|electrician|roofing|roofer)$/i;

export function parseContractorsTab(rows) {
  const out = section("contractors");
  let workType = "";

  for (const { cells, line } of rows) {
    if (isBlank(cells)) continue;
    const first = clean(cells[0]);
    if (norm(first) === "work type") continue;
    if (first) workType = first.replace(/:\s*$/, "").trim();

    const name = clean(cells[2]);
    const phone = clean(cells[3]);
    const notes = [];
    const addressParts = [];
    const companyCandidates = [];

    const companyLines = cellLines(cells[1]);
    for (const text of companyLines) {
      if (name && norm(text).startsWith(norm(name)) && norm(text) !== norm(name)) {
        notes.push(text.slice(name.length).replace(/^[\s,–-]+/, ""));
      } else if (companyLines.length > 1 && looksLikeAddressLine(text)) {
        addressParts.push(text);
      } else if (!GENERIC_TRADE_RE.test(text)) {
        companyCandidates.push(text);
      }
    }

    let email = "";
    let website = "";
    const extraLinks = [];
    for (const token of String(cells[4] ?? "").split(/[\s,;]+/).filter(Boolean)) {
      if (looksLikeEmail(token)) {
        if (!email) email = token;
        else notes.push(token);
      } else if (looksLikeUrl(token)) {
        extraLinks.push(token);
      } else {
        notes.push(token);
      }
    }
    const websiteCell = clean(cells[5]);
    if (websiteCell && looksLikeUrl(websiteCell)) {
      extraLinks.unshift(websiteCell);
    } else if (websiteCell) {
      notes.push(websiteCell);
    }
    if (extraLinks.length) {
      website = extraLinks[0];
      notes.push(...extraLinks.slice(1));
    }
    notes.push(clean(cells[6]));

    const companyName = companyCandidates[0] || name;
    notes.push(...companyCandidates.slice(1));

    if (!companyName && !phone && !email) {
      skip(out, line, "No contractor name or contact details", cells);
      continue;
    }

    out.rows.push(
      makeRow("contractors", line, {
        companyName,
        contactName: name,
        specialty: contractorSpecialty(`${workType} ${companyName}`.match(/appliance/i) ? "appliance" : workType).join(", "),
        phone,
        email,
        website,
        address: addressParts.join(", "),
        notes: joinNotes(notes),
      }),
    );
  }

  return { sections: [out] };
}

function expenseRow(line, fields, warnings = []) {
  const { date, propertyLabel, propertyAddress, description, categoryText, amount, extraNotes } = fields;
  const title = description || categoryText;
  const detail = categoryText && categoryText !== title && !PLAIN_CATEGORY_RE.test(categoryText) ? categoryText : "";
  const notes = [detail, ...extraNotes];
  if (propertyLabel && isGeneralLabel(propertyLabel)) {
    notes.push(`Sheet property: ${propertyLabel}`);
  }
  const rowWarnings = [...warnings];
  const amountText = moneyString(amount);
  if (!amountText) rowWarnings.push("No amount in the sheet — type one in to import this row.");
  if (!date) rowWarnings.push("No date in the sheet — add one to import this row.");

  return makeRow(
    "expenses",
    line,
    {
      date: date || "",
      propertyLabel: propertyLabel && !isGeneralLabel(propertyLabel) ? propertyLabel : "",
      propertyAddress: propertyAddress || "",
      title,
      category: inferExpenseCategory(categoryText, description),
      amount: amountText,
      notes: joinNotes(notes),
    },
    { warnings: rowWarnings },
  );
}

function headerIndex(cells) {
  const index = {};
  cells.forEach((cell, i) => {
    const key = norm(cell);
    if (key && index[key] === undefined) index[key] = i;
  });
  return index;
}

export function parseExpensesTab(rows) {
  const out = section("expenses");
  const headerRow = rows.find((row) => !isBlank(row.cells));
  if (!headerRow) return { sections: [out] };
  const idx = headerIndex(headerRow.cells);
  const get = (cells, name) => clean(cells[idx[name]]);

  for (const { cells, line } of rows) {
    if (line === headerRow.line || isBlank(cells)) continue;
    const description = get(cells, "description");
    const categoryText = get(cells, "category");
    const amount = get(cells, "amount");
    if (!description && !categoryText) {
      skip(out, line, amount ? "Amount without a description (looks like a subtotal)" : "Nothing to import", cells);
      continue;
    }
    out.rows.push(
      expenseRow(line, {
        date: parseSheetDate(get(cells, "date")),
        propertyLabel: get(cells, "property"),
        description,
        categoryText,
        amount,
        extraNotes: [get(cells, "receipt"), get(cells, "note")],
      }),
    );
  }
  return { sections: [out] };
}

/**
 * Per-property tabs (one tab per house): expense columns on the left, the
 * household (Tenants, DOB, Phone, Email) on the right.
 */
export function parsePropertyTab(rows) {
  const expenses = section("expenses");
  const tenants = section("tenants");
  const headerRow = rows.find((row) => !isBlank(row.cells));
  if (!headerRow) return { sections: [expenses, tenants] };
  const idx = headerIndex(headerRow.cells);
  const get = (cells, name) => (idx[name] === undefined ? "" : clean(cells[idx[name]]));

  let propertyLabel = "";
  let propertyAddress = "";
  for (const { cells, line } of rows) {
    if (line === headerRow.line) continue;
    propertyLabel = propertyLabel || get(cells, "property");
    propertyAddress = propertyAddress || get(cells, "address");
  }
  if (!propertyLabel && propertyAddress) {
    propertyLabel = parseFullAddress(propertyAddress)?.address || propertyAddress;
  }

  let lastDate = null;
  const people = [];
  for (const { cells, line } of rows) {
    if (line === headerRow.line || isBlank(cells)) continue;

    const person = get(cells, "tenants");
    if (person) {
      people.push({
        line,
        name: person,
        dob: get(cells, "dob"),
        phone: get(cells, "phone"),
        email: get(cells, "email"),
        leaseStart: get(cells, "lease start"),
        leaseEnd: get(cells, "lease end"),
        rent: get(cells, "rent") || get(cells, "monthly rent"),
        deposit: get(cells, "deposit") || get(cells, "security deposit"),
      });
    }

    const description = get(cells, "description");
    const categoryText = get(cells, "category");
    const amount = get(cells, "amount");
    const rawDate = get(cells, "date");
    const parsedDate = parseSheetDate(rawDate);
    if (!description && !categoryText) {
      if (amount) skip(expenses, line, "Amount without a description (looks like a subtotal)", cells.slice(0, 7));
      continue;
    }
    const warnings = [];
    let date = parsedDate;
    if (!date && !rawDate && lastDate) {
      date = lastDate;
      warnings.push("No date in the sheet — using the date from the row above.");
    }
    if (parsedDate) lastDate = parsedDate;

    expenses.rows.push(
      expenseRow(
        line,
        {
          date,
          propertyLabel: get(cells, "property") || propertyLabel,
          propertyAddress,
          description,
          categoryText,
          amount,
          extraNotes: [get(cells, "receipt"), get(cells, "note")],
        },
        warnings,
      ),
    );
  }

  if (people.length) {
    const primary = people.find((p) => p.phone || p.email) || people[0];
    const others = people.filter((p) => p !== primary);
    const [firstName, ...rest] = primary.name.split(" ");
    const household = others
      .map((p) => (p.dob ? `${p.name} (DOB ${p.dob})` : p.name))
      .join("; ");
    tenants.rows.push(
      makeRow(
        "tenants",
        primary.line,
        {
          firstName,
          lastName: rest.join(" "),
          phone: primary.phone,
          email: primary.email,
          propertyLabel,
          propertyAddress,
          leaseStartDate: parseSheetDate(primary.leaseStart) || "",
          leaseEndDate: parseSheetDate(primary.leaseEnd) || "",
          monthlyRent: moneyString(primary.rent),
          securityDeposit: moneyString(primary.deposit),
          notes: joinNotes([
            primary.dob ? `DOB ${primary.dob}` : "",
            household ? `Household: ${household}` : "",
          ]),
        },
        {
          warnings:
            primary.leaseStart && primary.leaseEnd && primary.rent
              ? []
              : ["The sheet has no lease dates or rent — fill them in to add this tenant."],
        },
      ),
    );
  }

  return { sections: [expenses, tenants] };
}

export function parseInsuranceTab(rows) {
  const policies = section("insurance");
  const expenses = section("expenses");
  let mode = "policies";
  let expenseIdx = null;

  for (const { cells, line } of rows) {
    if (isBlank(cells)) continue;
    const first = norm(cells[0]);

    if (first === "home address") continue;
    if (first === "inactive") {
      mode = "inactive";
      continue;
    }
    if (first === "date" && cells.map(norm).includes("description")) {
      mode = "expenses";
      expenseIdx = headerIndex(cells);
      continue;
    }

    if (mode === "inactive") {
      skip(policies, line, "Listed under Inactive in the sheet", cells);
      continue;
    }

    if (mode === "expenses") {
      const get = (name) => (expenseIdx[name] === undefined ? "" : clean(cells[expenseIdx[name]]));
      const description = get("description");
      if (!description) {
        skip(expenses, line, "Nothing to import", cells);
        continue;
      }
      expenses.rows.push(
        expenseRow(line, {
          date: parseSheetDate(get("date")),
          propertyLabel: get("property"),
          description,
          categoryText: "",
          amount: get("amount"),
          extraNotes: [get("note")],
        }),
      );
      continue;
    }

    const propertyLabel = clean(cells[0]);
    const insuranceName = clean(cells[1]);
    if (!propertyLabel || !insuranceName) {
      skip(policies, line, "Note without a policy", cells);
      continue;
    }
    const term = parseTerm(cells[3]);
    let agent = clean(cells[6]);
    const notes = [clean(cells[5])];
    if (agent && (/^\$/.test(agent) || /premium/i.test(agent))) {
      notes.push(agent);
      agent = "";
    }
    const warnings = [];
    if (!term.start || !term.end) warnings.push(`Couldn't read the policy term "${clean(cells[3])}".`);

    policies.rows.push(
      makeRow(
        "insurance",
        line,
        {
          propertyLabel,
          insuranceName,
          policyNumber: clean(cells[2]).replace(/^policy\s+/i, ""),
          termStart: term.start || "",
          termEnd: term.end || "",
          premium: moneyString(cells[4]),
          agent,
          notes: joinNotes(notes),
        },
        { warnings },
      ),
    );
  }

  return { sections: [policies, expenses] };
}

// --- Credentials -----------------------------------------------------------

const VAULT_GROUPS = {
  operation: "operations",
  operations: "operations",
  bills: "bills",
  "online shop": "operations",
  banking: "banking",
  credit: "banking",
  "credit cards": "banking",
  insurance: "operations",
  inactive: "operations",
};
const BILL_HINT_RE = /\b(bge|hoa|water|gas|electric|utility|utilities|sewer|trash)\b/i;
const ACCOUNT_LABEL_RE = /^acc(ount)?\.?\s*(number|num|no\.?|#)\s*$/i;
const ACCOUNT_VALUE_RE = /^acc(ount)?\.?\s*(number|num|no\.?|#)\s*[-:#]?\s*(\S.*)$/i;
const ACCOUNT_NAME_RE = /\b(ein|tax id|account (number|#|no))\b/i;
const SECRET_LABEL_RE = /\b(pin|password|passcode|pw|code)\b/i;
const LABELED_SECRET_RE = /^(.*?\b(?:pin|password|passcode|pw|code)\b)\s*[-:#]\s*(\S.*)$/i;
const PIN_RE = /\bpin\b/i;

function groupFor(label) {
  const key = norm(label);
  if (VAULT_GROUPS[key]) return { exact: true, section: VAULT_GROUPS[key] };
  for (const prefix of Object.keys(VAULT_GROUPS)) {
    if (key.startsWith(`${prefix} `) || key.startsWith(`${prefix}-`)) {
      return { exact: false, section: VAULT_GROUPS[prefix] };
    }
  }
  return null;
}

const isIdentifier = (value) =>
  looksLikeEmail(value) || looksLikeUrl(value) || looksLikePhone(value);

export function parseCredentialsTab(rows) {
  const out = section("vault");
  let group = "";
  let groupSection = "operations";
  let pendingHeader = "";
  let previous = null;

  const push = (line, entry, warnings = []) => {
    const data = {
      section: entry.section || groupSection,
      name: clean(entry.name),
      loginEmail: "",
      username: "",
      secret: clean(entry.secret),
      accountNumber: clean(entry.accountNumber),
      url: clean(entry.url),
      notes: joinNotes(entry.notes || []),
    };
    const login = clean(entry.login);
    if (login && looksLikeEmail(login)) data.loginEmail = login;
    else if (login) data.username = login;
    if (BILL_HINT_RE.test(data.name)) data.section = "bills";

    const hasDetails = data.loginEmail || data.username || data.secret || data.accountNumber || data.url || data.notes;
    if (!data.name || !hasDetails) {
      skip(out, line, "No login details", [entry.name || ""]);
      return null;
    }

    // The same login listed twice in the sheet becomes one entry.
    const loginKey = norm(data.loginEmail || data.username);
    const twin = out.rows.find(
      (row) =>
        textEq(row.data.name, data.name) &&
        norm(row.data.loginEmail || row.data.username) === loginKey &&
        (!row.data.secret || !data.secret || row.data.secret === data.secret),
    );
    if (twin) {
      for (const key of ["secret", "accountNumber", "url"]) {
        if (!twin.data[key] && data[key]) twin.data[key] = data[key];
      }
      twin.data.notes = joinNotes([twin.data.notes, data.notes]);
      return twin;
    }
    const rowWarnings = [...warnings];
    let defaultSelected = true;
    if (PIN_RE.test(entry.secretLabel || "") || PIN_RE.test(data.name)) {
      rowWarnings.push("Card PINs are safest memorized, not stored — this row is unticked.");
      defaultSelected = false;
    }
    const row = makeRow("vault", line, data, { warnings: rowWarnings, defaultSelected });
    out.rows.push(row);
    return row;
  };

  for (const { cells: rawCells, line } of rows) {
    const cells = rawCells.map(clean);
    if (isBlank(cells)) {
      // A blank row ends the current group ("Banking", "Insurance", …).
      group = "";
      groupSection = "operations";
      pendingHeader = "";
      continue;
    }
    const first = cells[0];
    const values = cells.slice(1);
    const filled = values.filter(Boolean);
    const grouping = first ? groupFor(first) : null;

    if (grouping?.exact) {
      group = first;
      groupSection = grouping.section;
    }

    // "Acc Number, 12345" belongs to the row above it.
    if (ACCOUNT_LABEL_RE.test(first) && previous) {
      previous.data.accountNumber = filled.join(" ");
      continue;
    }

    if (!filled.length) {
      if (!grouping) skip(out, line, "Label without details", cells);
      continue;
    }

    // "Credit Cards, Limit" is a mini header for the rows below it.
    if (grouping && filled.length === 1 && !/[\d@]/.test(filled[0]) && !values.slice(1).some(Boolean)) {
      pendingHeader = filled[0];
      continue;
    }

    // "Some Card, 5,000.00" under a "Limit" header → note on the matching login.
    if (!grouping && first && filled.length === 1 && looksLikeMoney(filled[0])) {
      const note = pendingHeader ? `${pendingHeader}: ${filled[0]}` : filled[0];
      const target = [...out.rows].reverse().find((row) => norm(row.data.name).startsWith(norm(first)));
      if (target) {
        target.data.notes = joinNotes([target.data.notes, note]);
      } else {
        previous = push(line, { name: first, notes: [note] });
      }
      continue;
    }

    let name;
    let ids;
    const label = first && !grouping?.exact ? first : "";
    if (!label) {
      [name, ...ids] = values;
    } else if (grouping) {
      // "Credit Cards - Personal, Some Card, …" → "Some Card (Credit Cards - Personal)"
      name = values[0] ? `${values[0]} (${label})` : label;
      ids = values.slice(1);
    } else if (SECRET_LABEL_RE.test(label) && filled.length === 1) {
      // "Locker code, 1234"
      previous = push(line, { name: label, secret: filled[0], secretLabel: label });
      continue;
    } else if (isIdentifier(values[0])) {
      // "Email, someone@…, password, …" / "Webpage, example.org, …"
      name = label;
      ids = values;
    } else {
      // "BGE, 12 Maple Rd, login, password"
      name = values[0] ? `${label} – ${values[0]}` : label;
      ids = values.slice(1);
    }

    const entry = { name, notes: [], url: "", login: "", secret: "", accountNumber: "" };
    if (group && !["operation", "operations", "bills"].includes(norm(group))) {
      entry.notes.push(group);
    }
    if (!entry.url && looksLikeUrl(name) && !name.includes(" ")) entry.url = name;

    const extraSecrets = [];
    const rest = [...ids];
    // Position 0 = login, position 1 = password, the rest = links / notes.
    let login = clean(rest.shift());
    let secret = clean(rest.shift());

    if (login && looksLikeUrl(login) && !looksLikeEmail(login)) {
      entry.url = entry.url || login;
      login = "";
    }
    if (ACCOUNT_NAME_RE.test(name) && login && !secret) {
      entry.accountNumber = login;
      login = "";
    }
    const loginSecret = login.match(LABELED_SECRET_RE);
    if (loginSecret) {
      // "ATM Pin - 0000" sitting in the login column.
      entry.secretLabel = loginSecret[1];
      entry.notes.push(loginSecret[1]);
      if (!secret) secret = loginSecret[2];
      else extraSecrets.push({ label: loginSecret[1], value: loginSecret[2] });
      login = "";
    }
    entry.login = login;
    entry.secret = secret;

    for (let i = 0; i < rest.length; i += 1) {
      const cell = clean(rest[i]);
      if (!cell) continue;
      const accountMatch = cell.match(ACCOUNT_VALUE_RE);
      const labeledSecret = cell.match(LABELED_SECRET_RE);
      if (looksLikeUrl(cell) && !entry.url) {
        entry.url = cell;
      } else if (accountMatch) {
        entry.accountNumber = accountMatch[3];
      } else if (labeledSecret) {
        extraSecrets.push({ label: labeledSecret[1], value: labeledSecret[2] });
      } else if (SECRET_LABEL_RE.test(cell) && clean(rest[i + 1])) {
        // "app pw", "abcd efgh …" → a second password with its own row.
        extraSecrets.push({ label: cell, value: clean(rest[i + 1]) });
        i += 1;
      } else {
        entry.notes.push(cell);
      }
    }

    const row = push(line, entry);
    if (row) previous = row;
    for (const extra of extraSecrets) {
      push(line, {
        name: `${name} – ${extra.label}`,
        login: entry.login,
        secret: extra.value,
        secretLabel: extra.label,
        section: row?.data.section,
      });
    }
  }

  return { sections: [out] };
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

const PARSERS = {
  contractors: parseContractorsTab,
  credentials: parseCredentialsTab,
  expenses: parseExpensesTab,
  property: parsePropertyTab,
  insurance: parseInsuranceTab,
};

/**
 * Parse one exported tab. `forcedTab` overrides detection when the owner picks
 * the tab type by hand.
 */
export function parseWorkbookFile(fileName, text, forcedTab) {
  return parseWorkbookRows(fileName, parseCsvWithLines(text), forcedTab);
}

/** Rows read from an .xlsx sheet (string[][]) → the { cells, line } shape the parsers use. */
export function sheetRowsToLines(rows) {
  return rows.map((cells, index) => ({ cells: cells || [], line: index + 1 }));
}

/** Parse already-split rows ({ cells, line }[]) from a CSV file or an Excel sheet. */
export function parseWorkbookRows(name, rows, forcedTab) {
  rowSeq = 0; // row ids are stable for the same file + tab
  const tab = forcedTab || detectTab(name, rows);
  if (!tab || !PARSERS[tab]) {
    return { tab: null, sections: [] };
  }
  const { sections } = PARSERS[tab](rows);
  return { tab, sections: sections.filter((s) => s.rows.length || s.skipped.length) };
}

// ---------------------------------------------------------------------------
// Planning: compare parsed rows with what's already in the account
// ---------------------------------------------------------------------------

const textEq = (a, b) => normLoose(a) === normLoose(b);
const recordText = (record) =>
  normLoose(
    Object.values(record || {})
      .filter((value) => typeof value === "string")
      .join(" "),
  );
/** Note parts from the sheet that aren't already somewhere on the record. */
function newNoteParts(existing, incoming) {
  const haystack = recordText(existing);
  return String(incoming ?? "")
    .split(" · ")
    .map(clean)
    .filter((part) => part && !haystack.includes(normLoose(part)));
}
function noteChange(changes, existing, incoming) {
  const parts = newNoteParts(existing, incoming);
  if (parts.length) changes.notes = joinNotes([existing.notes, ...parts]);
}
const websiteKey = (value) => norm(value).replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/+$/, "");

function resolvePropertyFor(data, propertyIndex) {
  const label = clean(data.propertyLabel);
  if (!label) return { action: "none" };
  const key = propertyKey(label);
  const property = key ? propertyIndex.get(key) : null;
  if (property) return { action: "link", id: property._id, key, label: property.name || property.address };
  const address = parseFullAddress(data.propertyAddress) || parseFullAddress(label);
  if (key && address && propertyKey(address.address) === key) {
    return { action: "create", key, label: address.address, address };
  }
  return { action: "missing", key, label };
}

function validate(kind, data, isNew, property) {
  const problems = [];
  const needDate = (value, label) => {
    if (!dateKeyToTimestamp(value)) problems.push(`${label} is missing`);
  };
  if (kind === "contractors") {
    if (!clean(data.companyName) && !clean(data.contactName)) problems.push("Company or contact is required");
  } else if (kind === "expenses") {
    needDate(data.date, "Date");
    if (!clean(data.title)) problems.push("Description is required");
    if (!(parseMoney(data.amount) > 0)) problems.push("Amount is missing");
  } else if (kind === "insurance") {
    if (!clean(data.propertyLabel)) problems.push("Property is required");
    if (!clean(data.insuranceName)) problems.push("Insurer is required");
    if (!clean(data.policyNumber)) problems.push("Policy # is required");
    needDate(data.termStart, "Term start");
    needDate(data.termEnd, "Term end");
  } else if (kind === "vault") {
    if (!clean(data.name)) problems.push("Name is required");
  } else if (kind === "tenants") {
    if (!clean(data.firstName)) problems.push("First name is required");
    if (isNew) {
      needDate(data.leaseStartDate, "Lease start");
      needDate(data.leaseEndDate, "Lease end");
      if (!(parseMoney(data.monthlyRent) > 0)) problems.push("Rent is missing");
      if (!property || property.action === "missing" || property.action === "none") {
        problems.push("Property isn't in your portfolio yet");
      }
    }
  }
  return problems;
}

function contractorScore(row, existing) {
  const companyMatch = clean(row.companyName) && textEq(row.companyName, existing.companyName);
  const contactMatch = clean(row.contactName) && textEq(row.contactName, existing.contactName);
  const phoneMatch = phoneKey(row.phone) && phoneKey(row.phone) === phoneKey(existing.phone);
  let score = (companyMatch ? 2 : 0) + (contactMatch ? 2 : 0) + (phoneMatch ? 1 : 0);
  if (
    !clean(row.contactName) &&
    (["office", ""].includes(norm(existing.contactName)) || textEq(existing.contactName, existing.companyName))
  ) {
    score += 1;
  }
  if (!clean(row.companyName) || textEq(row.companyName, row.contactName)) {
    // Person-only rows ("Pat") match on the person.
    if (contactMatch && textEq(existing.companyName, existing.contactName)) score += 2;
  }
  return score >= 3 ? score : 0;
}

/** Greedy one-to-one assignment of sheet rows to existing records by score. */
function assignMatches(rows, existing, scoreFn) {
  const pairs = [];
  rows.forEach((row, i) => {
    existing.forEach((record, j) => {
      const score = scoreFn(row.data, record);
      if (score > 0) pairs.push({ i, j, score });
    });
  });
  pairs.sort((a, b) => b.score - a.score || a.i - b.i || a.j - b.j);
  const rowTaken = new Map();
  const recordTaken = new Set();
  for (const pair of pairs) {
    if (rowTaken.has(pair.i) || recordTaken.has(pair.j)) continue;
    rowTaken.set(pair.i, existing[pair.j]);
    recordTaken.add(pair.j);
  }
  return rowTaken;
}

function diffFields(kind, data, existing) {
  const changes = {};
  const setIf = (key, value, same) => {
    if (clean(value) && !same) changes[key] = value;
  };
  if (kind === "contractors") {
    setIf("companyName", clean(data.companyName), textEq(data.companyName, existing.companyName));
    setIf("contactName", clean(data.contactName), textEq(data.contactName, existing.contactName));
    setIf("phone", clean(data.phone), phoneKey(data.phone) === phoneKey(existing.phone));
    setIf("email", clean(data.email), norm(data.email) === norm(existing.email));
    // Keep a website the owner already has; only fill it in when it's empty.
    setIf("website", clean(data.website), Boolean(clean(existing.website)));
    setIf("address", clean(data.address), textEq(data.address, existing.address));
    noteChange(changes, existing, data.notes);
    const current = existing.specialty || [];
    const incoming = splitList(data.specialty);
    const merged = [...current];
    for (const item of incoming) {
      if (!merged.some((existingItem) => textEq(existingItem, item))) merged.push(item);
    }
    if (merged.length !== current.length) changes.specialty = merged;
  } else if (kind === "insurance") {
    setIf("insuranceName", clean(data.insuranceName), textEq(data.insuranceName, existing.insuranceName));
    setIf("policyNumber", clean(data.policyNumber), textEq(data.policyNumber, existing.policyNumber));
    if (dateKeyToTimestamp(data.termStart) && data.termStart !== timestampToDateKey(existing.termStart)) {
      changes.termStart = dateKeyToTimestamp(data.termStart);
    }
    if (dateKeyToTimestamp(data.termEnd) && data.termEnd !== timestampToDateKey(existing.termEnd)) {
      changes.termEnd = dateKeyToTimestamp(data.termEnd);
    }
    const premium = parseMoney(data.premium);
    if (premium !== null && Math.round(premium * 100) !== Math.round((existing.premium || 0) * 100)) {
      changes.premium = premium;
    }
    setIf("agent", clean(data.agent), textEq(data.agent, existing.agent));
    noteChange(changes, existing, data.notes);
  } else if (kind === "vault") {
    setIf("loginEmail", clean(data.loginEmail), norm(data.loginEmail) === norm(existing.loginEmail));
    setIf("username", clean(data.username), clean(data.username) === clean(existing.username));
    setIf("secret", clean(data.secret), clean(data.secret) === clean(existing.secret));
    setIf("accountNumber", clean(data.accountNumber), textEq(data.accountNumber, existing.accountNumber));
    setIf("url", clean(data.url), websiteKey(data.url) === websiteKey(existing.url));
    noteChange(changes, existing, data.notes);
  } else if (kind === "tenants") {
    setIf("phone", clean(data.phone), phoneKey(data.phone) === phoneKey(existing.phone));
    setIf("email", clean(data.email), norm(data.email) === norm(existing.email));
    if (dateKeyToTimestamp(data.leaseStartDate) && data.leaseStartDate !== timestampToDateKey(existing.leaseStartDate)) {
      changes.leaseStartDate = dateKeyToTimestamp(data.leaseStartDate);
    }
    if (dateKeyToTimestamp(data.leaseEndDate) && data.leaseEndDate !== timestampToDateKey(existing.leaseEndDate)) {
      changes.leaseEndDate = dateKeyToTimestamp(data.leaseEndDate);
    }
    const rent = parseMoney(data.monthlyRent);
    if (rent !== null && rent !== existing.monthlyRent) changes.monthlyRent = rent;
    const deposit = parseMoney(data.securityDeposit);
    if (deposit !== null && deposit !== existing.securityDeposit) changes.securityDeposit = deposit;
    noteChange(changes, existing, data.notes);
  }
  return changes;
}

function splitList(value) {
  return String(value ?? "")
    .split(",")
    .map(clean)
    .filter(Boolean);
}

const SCORERS = {
  contractors: contractorScore,
  insurance: (row, existing) => {
    const sameProperty = propertyKey(row.propertyLabel) && propertyKey(row.propertyLabel) === propertyKey(existing.propertyLabel);
    if (!sameProperty) return 0;
    if (textEq(row.policyNumber, existing.policyNumber)) return 3;
    if (textEq(row.insuranceName, existing.insuranceName)) return 2;
    return 0;
  },
  vault: (row, existing) => {
    if (!textEq(row.name, existing.name)) return 0;
    const login = norm(row.loginEmail || row.username);
    const existingLogin = norm(existing.loginEmail || existing.username);
    if (login === existingLogin) return 3;
    return !login || !existingLogin ? 2 : 1;
  },
  tenants: (row, existing) => {
    const fullName = normLoose(`${row.firstName} ${row.lastName}`);
    if (fullName && fullName === normLoose(`${existing.firstName} ${existing.lastName}`)) return 3;
    if (clean(row.email) && norm(row.email) === norm(existing.email)) return 2;
    return 0;
  },
};

function expenseKey(dateKey, amount, title) {
  return `${dateKey}|${Math.round((amount || 0) * 100)}|${normLoose(title)}`;
}

/**
 * Work out what importing each row would do.
 *
 * Returns one plan per row: { status, problems, matchId, changes, property }
 *   status: "new" | "update" | "same" | "invalid"
 */
export function planRows(kind, rows, { existing = [], properties = [] } = {}) {
  const propertyIndex = buildPropertyIndex(properties);
  const plans = rows.map(() => ({ status: "new", problems: [], matchId: null, changes: {}, property: { action: "none" } }));

  if (kind === "expenses") {
    const byKey = new Map();
    for (const expense of existing) {
      const key = expenseKey(timestampToDateKey(expense.date), expense.amount, expense.title);
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(expense);
    }
    rows.forEach((row, i) => {
      const plan = plans[i];
      plan.property = resolvePropertyFor(row.data, propertyIndex);
      plan.problems = validate(kind, row.data, true, plan.property);
      if (plan.problems.length) {
        plan.status = "invalid";
        return;
      }
      const key = expenseKey(row.data.date, parseMoney(row.data.amount), row.data.title);
      const match = byKey.get(key)?.shift();
      if (match) {
        plan.status = "same";
        plan.matchId = match._id;
      }
    });
    return plans;
  }

  const matches = assignMatches(rows, existing, SCORERS[kind]);
  const seenKeys = new Map();
  rows.forEach((row, i) => {
    const plan = plans[i];
    const match = matches.get(i);
    if (kind === "tenants" || kind === "insurance") {
      plan.property = resolvePropertyFor(row.data, propertyIndex);
    }

    const dupKey = JSON.stringify(
      FIELD_SPECS[kind].map((field) => normLoose(row.data[field.key])),
    );
    if (seenKeys.has(dupKey)) {
      plan.status = "same";
      plan.problems = [`Same as line ${seenKeys.get(dupKey)} in this file`];
      return;
    }
    seenKeys.set(dupKey, row.line);

    plan.problems = validate(kind, row.data, !match, plan.property);
    if (plan.problems.length) {
      plan.status = "invalid";
      return;
    }
    if (match) {
      plan.matchId = match._id;
      plan.changes = diffFields(kind, row.data, match);
      if (kind === "insurance" && !match.propertyId && plan.property.action === "link") {
        plan.changes.propertyId = plan.property.id;
      }
      plan.status = Object.keys(plan.changes).length ? "update" : "same";
    }
  });
  return plans;
}

// ---------------------------------------------------------------------------
// Payloads for the Convex bulk import mutations
// ---------------------------------------------------------------------------

const optional = (value) => (clean(value) ? clean(value) : undefined);

function createPayload(kind, data, propertyId) {
  if (kind === "contractors") {
    const companyName = clean(data.companyName) || clean(data.contactName);
    return {
      companyName,
      contactName: clean(data.contactName) || companyName,
      phone: clean(data.phone),
      email: clean(data.email),
      website: optional(data.website),
      address: optional(data.address),
      notes: optional(data.notes),
      specialty: splitList(data.specialty),
    };
  }
  if (kind === "expenses") {
    const label = clean(data.propertyLabel);
    return {
      propertyId,
      title: clean(data.title),
      amount: parseMoney(data.amount),
      category: clean(data.category) || "other",
      date: dateKeyToTimestamp(data.date),
      notes: optional(joinNotes([data.notes, label && !propertyId ? `Property: ${label}` : ""])),
    };
  }
  if (kind === "insurance") {
    return {
      propertyId,
      propertyLabel: clean(data.propertyLabel),
      insuranceName: clean(data.insuranceName),
      policyNumber: clean(data.policyNumber),
      termStart: dateKeyToTimestamp(data.termStart),
      termEnd: dateKeyToTimestamp(data.termEnd),
      premium: parseMoney(data.premium) ?? 0,
      agent: optional(data.agent),
      notes: optional(data.notes),
    };
  }
  if (kind === "vault") {
    return {
      section: ["bills", "banking"].includes(data.section) ? data.section : "operations",
      name: clean(data.name),
      loginEmail: optional(data.loginEmail),
      username: optional(data.username),
      secret: optional(data.secret),
      accountNumber: optional(data.accountNumber),
      url: optional(data.url),
      notes: optional(data.notes),
    };
  }
  if (kind === "tenants") {
    return {
      propertyId,
      firstName: clean(data.firstName),
      lastName: clean(data.lastName),
      phone: clean(data.phone),
      email: clean(data.email),
      leaseStartDate: dateKeyToTimestamp(data.leaseStartDate),
      leaseEndDate: dateKeyToTimestamp(data.leaseEndDate),
      monthlyRent: parseMoney(data.monthlyRent) ?? 0,
      securityDeposit: parseMoney(data.securityDeposit) ?? 0,
      notes: optional(data.notes),
    };
  }
  throw new Error(`Unknown import kind: ${kind}`);
}

/**
 * Properties that have to be created before the selected rows can be linked.
 * Returns [{ key, name, address, city, state, zipCode, monthlyRent }].
 */
export function propertiesToCreate(items) {
  const byKey = new Map();
  for (const { kind, row, plan } of items) {
    if (plan.property?.action !== "create" || plan.status === "invalid" || plan.status === "same") continue;
    const existing = byKey.get(plan.property.key);
    const rent = kind === "tenants" ? parseMoney(row.data.monthlyRent) || 0 : 0;
    if (existing) {
      existing.monthlyRent = Math.max(existing.monthlyRent, rent);
    } else {
      byKey.set(plan.property.key, { key: plan.property.key, ...plan.property.address, monthlyRent: rent });
    }
  }
  return [...byKey.values()];
}

/**
 * Turn selected rows + plans into { creates, updates } for one kind.
 * `createdPropertyIds` maps property keys to ids created moments earlier.
 */
const compact = (object) =>
  Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined));

export function buildImportPayload(kind, items, createdPropertyIds = {}) {
  const creates = [];
  const updates = [];
  for (const { row, plan } of items) {
    if (plan.status === "new") {
      const propertyId =
        plan.property?.action === "link"
          ? plan.property.id
          : plan.property?.action === "create"
            ? createdPropertyIds[plan.property.key]
            : undefined;
      creates.push(compact(createPayload(kind, row.data, propertyId)));
    } else if (plan.status === "update") {
      updates.push(compact({ id: plan.matchId, ...plan.changes }));
    }
  }
  return { creates, updates };
}

// ---------------------------------------------------------------------------
// OneDrive live sync: plan a whole workbook against the account
// ---------------------------------------------------------------------------

export const SYNC_SOURCE = "onedrive";
export const SYNC_KIND_ORDER = ["contractors", "insurance", "expenses", "tenants", "vault"];
/** Records the sync may delete when their row disappears from the sheet. */
export const SYNC_REMOVABLE_KINDS = ["contractors", "insurance", "expenses", "vault"];
/** Above this many removals (and over half of a list) the sync waits for the owner. */
export const SYNC_REMOVAL_GUARD = 10;

/**
 * Plan a live sync from workbook sheets ([{ name, rows: string[][] }]).
 *
 * Rows that are new or changed are written; rows that need information are
 * reported as issues; records the sync created earlier (syncSource ===
 * "onedrive") whose row is gone from the sheet are removed — unless that would
 * remove a suspiciously large share of a list, which is held back instead.
 * Records the owner added in the app (no syncSource) are never removed.
 */
export function planWorkbookSync(sheets, { existing = {}, properties = [] } = {}) {
  const rowsByKind = {};
  const ignoredSheets = [];
  const usedSheets = [];
  for (const sheet of sheets) {
    const parsed = parseWorkbookRows(sheet.name, sheetRowsToLines(sheet.rows || []));
    if (!parsed.tab) {
      if ((sheet.rows || []).some((row) => row.some((cell) => clean(cell)))) ignoredSheets.push(sheet.name);
      continue;
    }
    usedSheets.push({ name: sheet.name, tab: parsed.tab });
    for (const sectionData of parsed.sections) {
      const list = (rowsByKind[sectionData.kind] ||= []);
      for (const row of sectionData.rows) list.push({ ...row, sheet: sheet.name });
    }
  }

  const issues = [];
  const kinds = {};
  const allItems = [];
  for (const kind of SYNC_KIND_ORDER) {
    const rows = rowsByKind[kind] || [];
    if (!rows.length) continue;
    const records = existing[kind] || [];
    const plans = planRows(kind, rows, { existing: records, properties });
    const items = [];
    const keep = new Set();
    const adoptIds = [];
    const counts = { created: 0, updated: 0, unchanged: 0, removed: 0, needsInfo: 0 };

    rows.forEach((row, index) => {
      const plan = plans[index];
      if (plan.matchId) keep.add(plan.matchId);
      if (plan.status === "invalid") {
        counts.needsInfo += 1;
        const leaseOnly = kind === "tenants" && plan.problems.every((p) => /Lease|Rent/.test(p));
        issues.push({
          sheet: row.sheet,
          line: row.line,
          message: leaseOnly
            ? "Tenant isn't on the Tenants page yet and the sheet has no lease dates or rent. Add Lease Start, Lease End and Rent columns, or add the tenant once on the Tenants page — after that the sheet keeps their details up to date."
            : plan.problems.join("; "),
        });
        return;
      }
      if (!row.defaultSelected) {
        issues.push({
          sheet: row.sheet,
          line: row.line,
          message: (row.warnings[0] || "Left out on purpose.").replace("this row is unticked", "left out of the sync"),
        });
        return;
      }
      if (plan.status === "new" || plan.status === "update") {
        items.push({ row, plan });
        allItems.push({ kind, row, plan });
        counts[plan.status === "new" ? "created" : "updated"] += 1;
      } else if (plan.status === "same") {
        counts.unchanged += 1;
        const record = plan.matchId && records.find((r) => r._id === plan.matchId);
        if (record && record.syncSource !== SYNC_SOURCE) adoptIds.push(record._id);
      }
    });
    let removals = [];
    let heldBack = 0;
    if (SYNC_REMOVABLE_KINDS.includes(kind)) {
      const synced = records.filter((record) => record.syncSource === SYNC_SOURCE);
      const gone = synced.filter((record) => !keep.has(record._id));
      if (gone.length > SYNC_REMOVAL_GUARD && gone.length > synced.length / 2) {
        heldBack = gone.length;
      } else {
        removals = gone.map((record) => record._id);
        counts.removed = removals.length;
      }
    }

    kinds[kind] = { items, adoptIds, removals, heldBack, counts };
  }

  return {
    kinds,
    issues,
    ignoredSheets,
    usedSheets,
    propertyCreates: propertiesToCreate(allItems),
  };
}

/** Compact, display-ready summary of a sync plan (stored on the connection). */
export function summarizeSyncPlan(plan) {
  return {
    counts: SYNC_KIND_ORDER.filter((kind) => plan.kinds[kind]).map((kind) => ({
      kind,
      ...plan.kinds[kind].counts,
      heldBack: plan.kinds[kind].heldBack,
    })),
    newProperties: plan.propertyCreates.map((property) => property.address),
    issues: plan.issues.slice(0, 50),
    ignoredSheets: plan.ignoredSheets,
    usedSheets: plan.usedSheets.map((sheet) => sheet.name),
  };
}
